import { comparer } from 'mobx';

import { AnalyticsStore, codeSplit, Controller, ExperimentStore, FetchOptions, FetchPageOptions, merge, PageStore } from 'bernie-core';
import { Localization } from 'bernie-l10n';
import { PageData } from 'bernie-http';

import { CollectionBusinessModel } from 'stores/multi-item-store-util';
import { CurrentHotelStore } from 'stores/current-hotel-store';
import { CustomerInterestsStore } from 'stores/customer-interests-store';
import { MultiItemStore } from 'stores/multi-item-store';
import { SearchCriteriaModel, SearchCriteriaUtils, singleUseQueryParams, untrackedQueryParams } from 'stores/models';
import { ServerDrivenUiStore } from 'sdui/sdui-store';

import { ExtendedContextStore } from 'types/flex-definitions';
import { PageName } from 'constants/pages';

import { buildFlexPageData } from 'utils/flex-seo-utils';
import { meta } from 'utils/meta';

import { roomsAndRatesLoadable } from 'components/infosite/rooms-and-rates/loadable';

const { withoutKeys, withKeys } = merge;

export const NAVIGATION_ID = 'hotel';

export class InfositeController implements Controller {
  public bundles = ['infosite'];
  public component = codeSplit(async () => {
    const routeComponent = import(/* webpackChunkName: "infosite" */ 'views/hotel/infosite.jsx');

    await (roomsAndRatesLoadable.codeSplit as any).loadRoute();

    return routeComponent;
  });
  public pageId = `${PageName.INFOSITE},H,30`;
  public path = '*/:lang?/:prefix?*h:hotelId.*';
  public exact = false;
  public routeName = 'infosite';
  public routeData = {
    rfrrNamespace: 'HOT.HIS',
    pageName: PageName.INFOSITE
  };
  public flexViewModelResponse;

  public fetchPageData(options: FetchPageOptions): Promise<PageData> {
    const context = options.context as ExtendedContextStore;
    const seoData = context && context.seoData;
    this.flexViewModelResponse = context && context.viewModelResponse;

    const navId = { navigationId: NAVIGATION_ID };

    // Either build SEO data from Flex (his-flex-web)
    if (this.flexViewModelResponse) {
      return Promise.resolve({
        ...buildFlexPageData(this.flexViewModelResponse, this.pageId),
        ...navId
      }) as Promise<PageData>;
    }

    // Or try to get data from Blacksmith from BFF
    if (seoData) {
      const seo = {
        seo: {
          canonical: seoData.url
        }
      };

      return Promise.resolve({
        ...seo,
        ...navId
      } as PageData);
    }

    return Promise.resolve(navId as PageData);
  }

  public async fetch(options: FetchOptions): Promise<any> {
    if (!options || !options.stores) {
      return Promise.reject('InfositeController.fetch received invalid FetchOptions');
    }

    const { isServer, stores, query, params } = options;

    const context: ExtendedContextStore = stores.get<ExtendedContextStore>('context');
    const page: PageStore = stores.get<PageStore>('page');
    const currentHotel: CurrentHotelStore = stores.get<CurrentHotelStore>('currentHotel');
    const sdui: ServerDrivenUiStore = stores.get<ServerDrivenUiStore>('sdui');
    const multiItem: MultiItemStore = stores.get<MultiItemStore>('multiItem');
    const multiItemBusinessModel: CollectionBusinessModel = multiItem && multiItem.cartInfo && multiItem.cartInfo.type;
    const analyticsStore: AnalyticsStore = stores.get<AnalyticsStore>('analytics');
    const experiments: ExperimentStore = stores.get<ExperimentStore>('experiment');
    const customerInterests: CustomerInterestsStore = stores.get<CustomerInterestsStore>('customerInterestsStore');

    const topReviewExperiment = experiments.exposures.PWA_HIS_DATELESS_PDP_Reviews_review_above_the_fold;
    currentHotel.shouldFetchTopReview = topReviewExperiment && topReviewExperiment.bucket === 2;

    multiItem.isMixMatchEnabled = query.mixMatch === 'true';

    const roomScoreExperiment = experiments.exposures.PWA_UGC_Room_Score;
    currentHotel.shouldFetchRoomScore = roomScoreExperiment && roomScoreExperiment.bucket === 1;

    const similarPropertiesExperiment = experiments.exposures.Flex_dateless_PDP_similar_hotels;
    const shouldFetchSimilarProperties = similarPropertiesExperiment && similarPropertiesExperiment.bucket === 1;

    const locationScoreExperiment = experiments.exposures.PWA_UGC_Location_Score;
    currentHotel.shouldFetchLocationScore = locationScoreExperiment && locationScoreExperiment.bucket === 1;

    const reviewsTravelerExperiment = experiments.exposures.PWA_HIS_DATED_PDP_Reviews_Alignment_By_Traveler_Type_Filters;
    currentHotel.shouldFetchTravelerType = reviewsTravelerExperiment && reviewsTravelerExperiment.bucket === 1;

    const commonPhrasesExperiment = experiments.exposures.PWA_HIS_DATELESS_PDP_Reviews_common_phrases_filter;
    currentHotel.shouldFetchKeyPhrases = commonPhrasesExperiment && commonPhrasesExperiment.bucket === 1;

    // infosite cares about hotelId URL param
    const { prevParams, ...nextParams } = params || { prevParams: undefined };

    // query params cleanup
    const singleUseCriteria: any = withKeys(singleUseQueryParams).merge({}, options.query);
    const untrackedKeys = [...untrackedQueryParams, ...singleUseCriteria, 'prevQuery'];
    const prevQuery = withoutKeys(untrackedKeys).merge({}, query.prevQuery);
    const nextQuery = withoutKeys(untrackedKeys).merge({}, query);

    if (multiItemBusinessModel && nextQuery) {
      nextQuery['businessModelFilter'] = multiItemBusinessModel;
    }

    const forceFetch = currentHotel.shouldFetch || currentHotel.shouldFetchOffers;

    if (forceFetch || !comparer.structural(nextParams, prevParams) || !comparer.structural(prevQuery, nextQuery)) {
      // TEMP FIX: check removal after 2018-05-01 with @dboerner
      this.removeRegionIdForSEMUrls(nextQuery);
      // =====================================================

      const parsedInfositeValues = SearchCriteriaUtils.parseInfositeValues(
        { ...nextQuery },
        new Localization(context.locale),
        context.localDateFormat
      );

      const incomingSearchCriteria = new SearchCriteriaModel(parsedInfositeValues);

      const criteriaChanged = this.haveCriteriaChanged(currentHotel.searchCriteria, incomingSearchCriteria);

      //33486_PWA_Large_Party_Mix_Match reset cart if rooms or dates criteria changes
      const mixMatchCriteriaChanged = this.hasRoomsOrDatesChanged(currentHotel.searchCriteria, incomingSearchCriteria);

      if (multiItem && multiItem.showMixMatchExperience && mixMatchCriteriaChanged) {
        multiItem.emptyCart(context).then(() => {
          if (analyticsStore) {
            analyticsStore.trackEvent('MultiItem.EmptyCart', 'MultiItem Cart Emptied', analyticsStore.omnitureData, null, true);
          }
        });
      }

      if (forceFetch || criteriaChanged || this.hasHotelChanged(prevParams, nextParams)) {
        currentHotel.setOfferData(withKeys(['startDate', 'endDate', 'adults', 'children']).merge({}, parsedInfositeValues));

        currentHotel.setHotelId(nextParams.hotelId);
        currentHotel.updateSearchCriteria(incomingSearchCriteria);
        if (!incomingSearchCriteria.isDateless) {
          currentHotel.clearDatelessContexts(context);
        }
        currentHotel.clearPoiData();

        const sduiExperiment = experiments.exposures.PWA_SDUI_TABBED_LAYOUT;
        const isSduiEnabled = sduiExperiment && sduiExperiment.bucket === 1;
        const layoutFetch = isSduiEnabled ? sdui.fetch(context) : Promise.resolve();

        const currentHotelFetch = currentHotel.fetch(analyticsStore, context, experiments, singleUseCriteria);
        const flexViewModelResponse = this.flexViewModelResponse;
        const similarHotelsFetchPromise = shouldFetchSimilarProperties
          ? currentHotel.fetchSimilarHotelsDateless(context, flexViewModelResponse)
          : Promise.resolve();

        if (
          flexViewModelResponse &&
          flexViewModelResponse.composition &&
          flexViewModelResponse.composition.page &&
          flexViewModelResponse.composition.page.regionList.length >= 1 &&
          flexViewModelResponse.composition.page.regionList[0].name === 'rooms' &&
          flexViewModelResponse.composition.page.regionList[0].modules.some(module => module.name === 'breadcrumbs')
        ) {
          currentHotel.setBreadcrumbsModel(flexViewModelResponse.composition.page.regionList);
        }

        //Only fetch reviews, when it hasn't been fetched yet
        const reviewsFetch = !currentHotel.hasPreFetchedReviews && currentHotel.fetchReviews(context, nextParams.hotelId, true);

        const locationScoreFetch =
          currentHotel &&
          currentHotel.shouldFetchLocationScore &&
          currentHotel.fetchPropertyLocationScoreInfo(context, currentHotel.hotelId);

        const keyPhraseLimit = context.deviceInformation.mobile ? 6 : null;
        const keyPhrasesFetch =
          currentHotel &&
          currentHotel.shouldFetchKeyPhrases &&
          currentHotel.fetchPropertyKeyPhrases(context, currentHotel.hotelId, keyPhraseLimit);

        const roomScoreFetch =
          currentHotel && currentHotel.shouldFetchRoomScore && currentHotel.fetchPropertyRoomScoreInfo(context, currentHotel.hotelId);

        if (roomScoreFetch) {
          await roomScoreFetch;
        }
        if (keyPhrasesFetch) {
          await keyPhrasesFetch;
        }

        await layoutFetch;
        await currentHotelFetch;
        await similarHotelsFetchPromise;

        if (locationScoreFetch) {
          await locationScoreFetch;
        }

        if (!page.seo) {
          const pageFetch = page.fetch(meta['hotel-infosite'], context.site, context.locale);
          await pageFetch;
        }

        if (reviewsFetch) {
          await reviewsFetch;
        }
      }

      page.title = page.title || currentHotel.hotel.detailsPageTitle;

      if (!isServer) {
        currentHotel.fetchSimilarHotels(context, experiments, multiItem.multiItemContext, customerInterests);
      }
    }
  }

  public async willDeactivate(options: FetchOptions): Promise<any> {
    if (!options || !options.stores) {
      return Promise.reject('InfositeController.willDeactivate receieved an invalid StoreContainer');
    }

    const currentHotel: CurrentHotelStore = options.stores.get<CurrentHotelStore>('currentHotel');
    const context: ExtendedContextStore = options.stores.get<ExtendedContextStore>('context');

    if (currentHotel.isFailure || !currentHotel.offers || currentHotel.offers.error || !currentHotel.hotel || currentHotel.hotel.error) {
      currentHotel.shouldFetch = true;
    }

    currentHotel.clearRoomsFromOffers();
    currentHotel.shouldFetchOffers = true;
    currentHotel.loadingOffers = true;
    delete context.footer;
  }

  private removeRegionIdForSEMUrls(query): any {
    // TODO: SEM regionId fix (do not remove until SEM URLs have been fixed)
    if (query && /google/gi.test(query.semcid)) {
      delete query.regionId;
    }
  }

  private hasHotelChanged(prevParams, nextParams): boolean {
    if (prevParams && nextParams && prevParams.hotelId && nextParams.hotelId) {
      return prevParams.hotelId !== nextParams.hotelId;
    }

    return false;
  }

  private haveCriteriaChanged(previousModel: SearchCriteriaModel, newModel: SearchCriteriaModel): boolean {
    if (Boolean(newModel) && !Boolean(previousModel)) {
      return true;
    }

    return !comparer.structural(previousModel.toJSON(), newModel.toJSON());
  }

  private hasRoomsOrDatesChanged(previousModel: SearchCriteriaModel, newModel: SearchCriteriaModel): boolean {
    if (newModel && !previousModel) {
      return true;
    }
    const startDateChanged = previousModel.startDate !== newModel.startDate;
    const endDateChanged = previousModel.endDate !== newModel.endDate;

    return !comparer.structural(previousModel.rooms, newModel.rooms) || startDateChanged || endDateChanged;
  }
}
